import type { Patch as ImmerPatch } from "immer";

export function iterableHasAtLeast<T>(collection: Iterable<T>, threshold = 1) {
  let count = 0;
  if (threshold < 0) threshold = 0;
  for (const _ of collection) {
    count++;
    if (count >= threshold) return true;
  }
  return false;
}

export function updateObjectInMap<T>(map: Map<T, any>, key: T, update: any) {
  if (map.has(key)) map.set(key, Object.assign(map.get(key), update));
  else map.set(key, update);
}

export function partition<T>(list: Iterable<T>, testFn: (item: T) => boolean) {
  const passed = [],
    failed = [];
  for (const item of list) {
    if (testFn(item)) passed.push(item);
    else failed.push(item);
  }
  return { passed, failed } as const;
}

type PredicateType<T> = null | undefined | ((item: T) => number);

function createValueGetter<T>(predicate: PredicateType<T>): (item: T) => number {
  return predicate ?? ((item) => +item);
}

export function minIndex<T>(list: Iterable<T>, predicate: PredicateType<T>, secondary?: PredicateType<T>) {
  const valueOf = createValueGetter(predicate);
  const secondaryValue = createValueGetter(secondary);
  let best: T,
    index = -1,
    min = Number.MAX_SAFE_INTEGER;

  let i = 0;
  for (const item of list) {
    const itemValue = valueOf(item);
    const betterCandidate =
      Math.abs(itemValue - min) > 1e-5 ? itemValue < min : secondaryValue(item) < secondaryValue(best!);
    if (betterCandidate) {
      best = item;
      min = itemValue;
      index = i;
    }
    i++;
  }
  return index;
}

export function maxIndex<T>(list: Iterable<T>, predicate: PredicateType<T>, secondary?: PredicateType<T>): number {
  const valueOf = createValueGetter(predicate);
  const secondaryValue = createValueGetter(secondary);
  let best: T,
    index = -1,
    max = Number.MIN_SAFE_INTEGER;

  let i = 0;
  for (const item of list) {
    const itemValue = valueOf(item);
    const betterCandidate =
      Math.abs(itemValue - max) > 1e-5 ? itemValue > max : secondaryValue(item) > secondaryValue(best!);
    if (betterCandidate) {
      best = item;
      max = itemValue;
      index = i;
    }
    i++;
  }
  return index;
}

export class MutationInfo {
  element: any;
  patches: ImmerPatch[];
  inversePatches: ImmerPatch[];

  constructor(element: any) {
    this.element = element;
    this.patches = [];
    this.inversePatches = [];
  }

  static fromFinalPatch([element, patch, inversePatch]: readonly [any, ImmerPatch[], ImmerPatch[]]) {
    const mutation = new MutationInfo(element);
    mutation.patches = patch;
    mutation.inversePatches = inversePatch;
    return mutation;
  }

  addMutation([element, patch, inversePatch]: readonly [any, ImmerPatch[], ImmerPatch[]]) {
    this.element = element;
    this.patches.push(...patch);
    this.inversePatches.unshift(...inversePatch);
  }
  //can add compressPatches functions - see immer documentation
}

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
